import React, {useState} from 'react';
import {Box, Typography, Grid, Tabs, Tab} from '@mui/material';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PeopleIcon from '@material-ui/icons/People';
import StorageIcon from '@material-ui/icons/Storage';
import PartnerDashboardShell from './PartnerDashboard.comp';
import {TIER_COLOR, READINESS_BAND_COLORS, CONTEXT_BAND_COLORS, RISK_BAND_COLORS} from '../constants';
import { useDashboard } from '../../../context/DashboardContext';

const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const GREEN  = '#1B6B55';
const BLUE   = '#3B6FA0';

/* ── Band palettes — sourced from src/utils/constants.js so every
   dashboard's legend stays in sync. ─────────────────────────────── */
const READINESS_COLORS = READINESS_BAND_COLORS;
const RISK_COLORS      = [RISK_BAND_COLORS.Monitor, RISK_BAND_COLORS.Watch, RISK_BAND_COLORS.Elevated, RISK_BAND_COLORS['Priority Concern']];
const CONTEXT_COLORS   = [CONTEXT_BAND_COLORS.Strong, CONTEXT_BAND_COLORS.Adequate, CONTEXT_BAND_COLORS.Limited, CONTEXT_BAND_COLORS.Low];

/* ── Reusable pieces ───────────────────────────────────────────── */
export const SectionHeader = ({eyebrow, title, subtitle}) => (
    <Box sx={{mb: 3}}>
        {eyebrow && (
            <Typography sx={{
                fontSize: '11px', fontWeight: 800, letterSpacing: '0.8px',
                textTransform: 'uppercase', color: ORANGE, mb: 0.5,
            }}>
                {eyebrow}
            </Typography>
        )}
        <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.25}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#6B7280', mt: 0.75, lineHeight: 1.6}}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

// `text` may be a single string (one flowing sentence) or an array of
// distinct items — arrays render one per line instead of running together.
export const NoteBanner = ({label, text}) => (
    <Box sx={{
        mt: 4, p: {xs: 2, md: 2.5}, borderRadius: '10px',
        backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderLeft: `4px solid ${NAVY}`,
    }}>
        {label && <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mb: 0.5}}>{label}</Typography>}
        {Array.isArray(text) ? (
            text.map((line, i) => (
                <Typography key={i} sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{line}</Typography>
            ))
        ) : (
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{text}</Typography>
        )}
    </Box>
);

const Chip = ({text, color}) => (
    <Box sx={{
        backgroundColor: color, color: '#ffffff', fontSize: '12px', fontWeight: 700,
        px: 1.5, py: 0.5, borderRadius: '6px', whiteSpace: 'nowrap',
    }}>
        {text}
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Tabbed page shell — used by every Overview sub-page
   ═════════════════════════════════════════════════════════════════ */
export const TabbedOverviewPage = ({tabs, skipNoDataGate = true}) => {
    const [activeTab, setActiveTab] = useState(0);
    const current = tabs[activeTab] || tabs[0];

    const tabsElement = tabs.length > 1 ? (
        <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
            sx={{
                borderBottom: '1px solid #E5E7EB',
                mb: 1,
                '& .MuiTab-root': {
                    textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2,
                    whiteSpace: 'nowrap', minHeight: 48, maxWidth: 500,
                },
                '& .Mui-selected': {color: `${ORANGE} !important`},
            }}
        >
            {tabs.map((t) => <Tab key={t.label} label={t.label}/>)}
        </Tabs>
    ) : null;

    return (
        <PartnerDashboardShell skipNoDataGate={skipNoDataGate} tabs={tabsElement}>
            <Box sx={{mt: tabs.length > 1 ? 3 : 0}}>
                {current.content}
            </Box>
        </PartnerDashboardShell>
    );
};

/* ══════════════════════════════════════════════════════════════════
   Slide 2 — The COS Assessment Suite
   ═════════════════════════════════════════════════════════════════ */
const SUITE_GROUPS = [
    {num: '1', title: 'Children',       items: ['Online safety readiness', 'Cyber risk experiences', 'Support and help-seeking environment']},
    {num: '2', title: 'Parents',        items: ['Digital parenting readiness', 'Home access and safety practices', 'Support network']},
    {num: '3', title: 'Teachers',       items: ['Risk Identification', 'Prevention and response', 'Classroom Online safety education']},
    {num: '4', title: 'School leaders',  items: ['Curriculum', 'Policies and governance', 'Reporting pathways and safeguarding practice']},
];

const CARD_SECTION_ASSESSMENT_SUITE = 'S_2';

export const AssessmentSuiteSection = () => {
    const { dashboardData } = useDashboard({ card_sections: CARD_SECTION_ASSESSMENT_SUITE });
    const sectionData = dashboardData?.cards?.sections?.find(s => s.section_code === CARD_SECTION_ASSESSMENT_SUITE);
    
    const titleCard = sectionData?.cards?.find(c => c.card_type === 'TitleCard');
    const stepCards = sectionData?.cards?.filter(c => c.card_type === 'StepCard') || [];
    const hasDynamicSteps = stepCards.length > 0;

    return (
        <Box>
            <SectionHeader
                title={titleCard?.title || "Child Online Safety (COS) Assessments"}
                subtitle={titleCard?.footer || "The Child Online Safety Assessments include four ready-to-deploy assessment packs that help clients establish a child online safety baseline, identify priority gaps, and benchmark performance against national and global reference points."}
            />

            <Grid container spacing={2.5}>
                {hasDynamicSteps ? (
                    stepCards.map((card, index) => (
                        <Grid item xs={12} sm={6} md={stepCards.length > 4 ? 4 : 3} key={card.card_code || index}>
                            <Box sx={{
                                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                                display: 'flex', flexDirection: 'column', gap: 1.5,
                            }}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                    {card.step_img ? (
                                        <Box
                                            component="img"
                                            src={card.step_img}
                                            sx={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }}
                                            alt={card.step_name}
                                        />
                                    ) : (
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: '50%', backgroundColor: GREEN,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}>
                                            <Typography sx={{fontSize: '17px', fontWeight: 800, color: '#fff'}}>{card.step_num || index + 1}</Typography>
                                        </Box>
                                    )}
                                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{card.step_name}</Typography>
                                </Box>
                                <Box
                                    sx={{
                                        m: 0, pl: 2.2,
                                        '& ul': { m: 0, pl: 0, listStyleType: 'disc' },
                                        '& li': { mb: 0.8, fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: card.step_html }}
                                />
                            </Box>
                        </Grid>
                    ))
                ) : (
                    SUITE_GROUPS.map((g) => (
                        <Grid item xs={12} sm={6} md={3} key={g.num}>
                            <Box sx={{
                                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                                display: 'flex', flexDirection: 'column', gap: 1.5,
                            }}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: '50%', backgroundColor: GREEN,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>
                                        <Typography sx={{fontSize: '17px', fontWeight: 800, color: '#fff'}}>{g.num}</Typography>
                                    </Box>
                                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{g.title}</Typography>
                                </Box>
                                <Box component="ul" sx={{m: 0, pl: 2.2}}>
                                    {g.items.map((it) => (
                                        <Box component="li" key={it} sx={{mb: 0.8}}>
                                            <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.5}}>{it}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    ))
                )}
            </Grid>

            <NoteBanner text="Each group receives role-specific assessments, scorecards, and dashboard views to support targeted action."/>
        </Box>
    );
};

/* ── What Does the COS Children Assessment Measure? ──────────────── */
const parseHeadlineHtml = (html) => {
    if (!html) return null;
    let clean = html.replace(/^<p>/, '').replace(/<\/p>$/, '');
    const parts = clean.split(/<br\s*\/?>\s*<br\s*\/?>/i);
    let questionHtml = parts[0] || '';
    let badgeHtml = parts[1] || '';
    const badgeText = badgeHtml.replace(/<[^>]+>/g, '').trim();
    return {
        questionHtml,
        badgeText
    };
};

// "Are students prepared? Do they have…" -> bold lead question on its own line,
// then the rest (PD slide 46, matching the mockup).
const splitLeadQuestion = (text) => {
    const clean = String(text || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    const idx = clean.indexOf('?');
    if (idx === -1) return {lead: '', rest: clean};
    return {lead: clean.slice(0, idx + 1), rest: clean.slice(idx + 1).trim()};
};

const MeasureCard = ({letter, color, title, question, badge, htmlContent, children}) => {
    const parsed = parseHeadlineHtml(htmlContent);
    const {lead, rest} = splitLeadQuestion(parsed ? parsed.questionHtml : question);
    const displayBadge = parsed ? parsed.badgeText : badge;

    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
            display: 'flex', flexDirection: 'column',
        }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
                <Box sx={{
                    width: 42, height: 42, borderRadius: '50%', backgroundColor: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Typography sx={{fontSize: '18px', fontWeight: 800, color: '#fff'}}>{letter}</Typography>
                </Box>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{title}</Typography>
            </Box>

            <Box sx={{mb: 1.5, minHeight: '72px'}}>
                {lead && <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.5}}>{lead}</Typography>}
                {rest && <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{rest}</Typography>}
            </Box>

            {displayBadge && (
                <Box sx={{
                    alignSelf: 'flex-start', backgroundColor: `${color}15`, color,
                    fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: '6px', mb: 2,
                }}>
                    {displayBadge}
                </Box>
            )}
            <Box sx={{borderTop: '1px solid #F3F4F6', pt: 2, flex: 1}}>{children}</Box>
        </Box>
    );
};

// Fallback list content — reproduced verbatim from the Dashboard Common
// Section deck (slide 5), shared with every other dashboard's identical
// "About COS Children Assessment" page. Used only when the backend hasn't
// supplied list_objects for these cards yet.
const FALLBACK_READINESS_AREAS = [
    'Youth AI-Digital Activities',
    'Self-Regulated AI Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
    'Authentic and Ethical Digital Identity',
    'Critical Information Management',
].map((name, idx) => ({code: `readiness-${idx}`, name, num: idx + 1}));

const FALLBACK_CONTEXT_AREAS = [
    'Digital Access at Home and School',
    'Youth Perception of Home and Support Environment',
    'Youth Perception of Parental Mediation',
    'Youth Perception of School AI-Digital Literacy Education',
].map((name, idx) => ({code: `context-${idx}`, name, num: idx + 1}));

const FALLBACK_CYBER_RISK_GROUPS = [
    {isMain: true, items: [{code: 'risk-1', name: 'Any Cyber Risk'}]},
    {category: 'CONDUCT RISK', items: [
        {code: 'risk-2', name: 'Cyberbullying'},
        {code: 'risk-3', name: 'Cybervictimization'},
        {code: 'risk-4', name: 'AI Image and Voice Misuse Victimization'},
    ]},
    {category: 'CONTENT RISK', items: [
        {code: 'risk-5', name: 'Sexual Content Exposure'},
        {code: 'risk-6', name: 'Sexual Contact Exposure'},
        {code: 'risk-7', name: 'AI Misinformation and Deceptive Content Risk'},
    ]},
    {category: 'CONTACT RISK', items: [
        {code: 'risk-8', name: 'Offline meetings with Online Strangers'},
        {code: 'risk-9', name: 'Sexual Conversation'},
        {code: 'risk-10', name: 'Unwanted Online Sexual Contact'},
        {code: 'risk-11', name: 'AI-Generated Sexual Image Risk'},
    ]},
    {category: 'CONTRACT & DATA RISK', items: [
        {code: 'risk-12', name: 'Misuse of Personal Information'},
        {code: 'risk-13', name: 'Underage Technology Use'},
    ]},
    {category: 'WELLBEING & SCREEN USE RISK', items: [
        {code: 'risk-14', name: 'Excessive Screen Time'},
        {code: 'risk-15', name: 'Video Game Addiction Risk'},
        {code: 'risk-16', name: 'Online Technology Addiction Risk'},
        {code: 'risk-17', name: 'AI Emotional Reliance'},
    ]},
];

const FALLBACK_ASSESSMENT_FOOTNOTE = (
    <>
        <p>* The risk definitions used in this assessment are consistent with the <strong>CPC Index global framework</strong>.</p>
        <p>The rubric tiers are based on each country’s percentile position within the 2017–2025 global reference distribution. National and global benchmark scores are aligned with the CPC Index methodology.</p>
        <p><strong>Any Cyber Risk</strong> refers to the proportion of children who experienced at least one of the cyber risks measured in the 2025 CPC Index, including content, contact, conduct, and contract risks including (Risks 2, 3, 5, 6, 8, 9, 10, 12, 13, 14, 15, 16).<br/>
        <strong>AI-related risks</strong> (Risks 4, 7, 11, 17) have been newly introduced in this assessment toolkit. Because sufficient historical and internationally comparable data are not yet available, national and global benchmark scores are not currently provided for these indicators.</p>
        <p><strong>CPC Index global benchmark:</strong> <a href="https://cpc.gcforum.org/global_cpc" target="_blank" rel="noreferrer">https://cpc.gcforum.org/global_cpc</a></p>
    </>
);

export const WhatItMeasuresSection = () => {
    const { dashboardData } = useDashboard({ card_sections: CARD_SECTION_ASSESSMENT_SUITE });
    const sectionData = dashboardData?.cards?.sections?.find(s => s.section_code === CARD_SECTION_ASSESSMENT_SUITE || s.section_code === 'S2');
    
    const titleCard = sectionData?.cards?.find(c => c.card_code === 'C_6a');
    const headlineA = sectionData?.cards?.find(c => c.card_code === 'C_7');
    const listA     = sectionData?.cards?.find(c => c.card_code === 'C_7a');
    const headlineB = sectionData?.cards?.find(c => c.card_code === 'C_8');
    const listB     = sectionData?.cards?.find(c => c.card_code === 'C_8a');
    const headlineC = sectionData?.cards?.find(c => c.card_code === 'C_9');
    const listC     = sectionData?.cards?.find(c => c.card_code === 'C_9a');

    // C_10 — methodology/benchmark footnote card, rendered below the measure grid.
    const headlineD = sectionData?.cards?.find(c => c.card_code === 'C_10');

    const dynamicReadinessAreas = Array.isArray(listA?.list_objects) && listA.list_objects.length > 0
        ? listA.list_objects.map(item => {
            const numMatch = typeof item.subname === 'string' ? item.subname.match(/\d+/) : null;
            const num = numMatch ? parseInt(numMatch[0], 10) : null;
            return {
                code: item.code,
                name: item.name,
                num
            };
        }).sort((a, b) => {
            if (a.num !== null && b.num !== null) return a.num - b.num;
            return 0;
        })
        : FALLBACK_READINESS_AREAS;

    const rawListB = listB?.list_objects;
    const cyberRiskGroups = Array.isArray(rawListB) && rawListB.length > 0
        ? (() => {
            const groupMap = new Map();
            rawListB.forEach(item => {
                const parentKey = item.parent_name || item.parent_code || 'Headline';
                const areaNumMatch = typeof item.parent_subname === 'string' ? item.parent_subname.match(/\d+/) : null;
                const areaNum = areaNumMatch ? parseInt(areaNumMatch[0], 10) : 99;

                const itemNumMatch = typeof item.subname === 'string' ? item.subname.match(/\d+/) : null;
                const itemNum = itemNumMatch ? parseInt(itemNumMatch[0], 10) : null;

                if (!groupMap.has(parentKey)) {
                    groupMap.set(parentKey, {
                        parent_code: item.parent_code,
                        parent_name: item.parent_name,
                        areaNum,
                        isMain: parentKey.toLowerCase() === 'headline' || item.parent_code?.toLowerCase() === 'headline',
                        category: (item.parent_name || '').toUpperCase(),
                        items: []
                    });
                }
                groupMap.get(parentKey).items.push({
                    code: item.code,
                    name: item.name,
                    itemNum
                });
            });

            const groups = Array.from(groupMap.values()).sort((a, b) => a.areaNum - b.areaNum);
            groups.forEach(g => {
                g.items.sort((a, b) => {
                    if (a.itemNum !== null && b.itemNum !== null) return a.itemNum - b.itemNum;
                    return 0;
                });
            });
            return groups;
        })()
        : FALLBACK_CYBER_RISK_GROUPS;

    const dynamicContextAreas = Array.isArray(listC?.list_objects) && listC.list_objects.length > 0
        ? listC.list_objects.map(item => {
            const numMatch = typeof item.subname === 'string' ? item.subname.match(/\d+/) : null;
            const num = numMatch ? parseInt(numMatch[0], 10) : null;
            return {
                code: item.code,
                name: item.name,
                num
            };
        }).sort((a, b) => {
            if (a.num !== null && b.num !== null) return a.num - b.num;
            return 0;
        })
        : FALLBACK_CONTEXT_AREAS;

    return (
        <Box>
            <SectionHeader
                title={titleCard?.title?.trim() || "What Does the COS Children Assessment Measure?"}
                subtitle={titleCard?.footer || "What do we need to understand to better support students in staying safe in the digital world?"}
            />
            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="A" color={GREEN}
                        title={headlineA?.headline_title || "Child Online Safety Readiness"}
                        question="Are students prepared? Do they have the readiness to participate safely and responsibly in today’s AI-powered digital world?"
                        badge="8 Areas"
                        htmlContent={headlineA?.headline_html}
                    >
                        <Box component="ol" sx={{ m: 0, pl: 2.2 }}>
                            {dynamicReadinessAreas.map((item, idx) => (
                                <Box component="li" key={item.code || idx} value={item.num || undefined} sx={{ mb: 0.7, '&::marker': { color: GREEN, fontSize: '12.5px' } }}>
                                    <Typography sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5 }}>
                                        {item.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="B" color={ORANGE}
                        title={headlineB?.headline_title || "Child Cyber-Risk Experiences *"}
                        question="What risks are students facing? How often do they report experiencing online risks in the past year?"
                        badge="17 Child Cyber-Risk Measures"
                        htmlContent={headlineB?.headline_html}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, pl: 2.2 }}>
                            {(() => {
                                let itemCounter = 0;
                                return cyberRiskGroups.map((group, idx) => {
                                    if (group.isMain) {
                                        const itemsToMap = group.items || [{ name: group.name }];
                                        return itemsToMap.map((item, itemIdx) => {
                                            itemCounter++;
                                            const itemName = typeof item === 'string' ? item : item.name;
                                            return (
                                                <Typography key={item.code || itemIdx} sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.6 }}>
                                                    {itemCounter}. {itemName}
                                                </Typography>
                                            );
                                        });
                                    }
                                    return (
                                        <Box key={idx} sx={{ mb: 0.5 }}>
                                            <Typography sx={{ 
                                                fontSize: '10.5px', 
                                                fontWeight: 800, 
                                                letterSpacing: '0.5px', 
                                                textTransform: 'uppercase',
                                                color: ORANGE, 
                                                mt: 1, 
                                                mb: 0.4 
                                            }}>
                                                {group.category}
                                            </Typography>
                                            {group.items.map((item, itemIdx) => {
                                                itemCounter++;
                                                const itemName = typeof item === 'string' ? item : item.name;
                                                return (
                                                    <Typography key={item.code || itemIdx} sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.6 }}>
                                                        {itemCounter}. {itemName}
                                                    </Typography>
                                                );
                                            })}
                                        </Box>
                                    );
                                });
                            })()}
                        </Box>
                    </MeasureCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="C" color={BLUE}
                        title={headlineC?.headline_title || "Context & Support"}
                        question="What supports do they have? Do families and schools help students stay safe?"
                        badge="4 Areas"
                        htmlContent={headlineC?.headline_html}
                    >
                        <Box component="ol" sx={{ m: 0, pl: 2.2 }}>
                            {dynamicContextAreas.map((item, idx) => (
                                <Box component="li" key={item.code || idx} value={item.num || undefined} sx={{ mb: 0.7, '&::marker': { color: BLUE, fontSize: '12.5px' } }}>
                                    <Typography sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5 }}>
                                        {item.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>
            </Grid>

            <Box sx={{
                mt: 3, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3,
                fontSize: '13px', color: '#374151', lineHeight: 1.6, '& p': {m: 0, mb: 1, '&:last-child': {mb: 0}},
            }}>
                {headlineD?.headline_html ? (
                    <Box dangerouslySetInnerHTML={{__html: headlineD.headline_html}}/>
                ) : FALLBACK_ASSESSMENT_FOOTNOTE}
            </Box>
        </Box>
    );
};

/* ══════════════════════════════════════════════════════════════════
   Slide 4 — COS Children Assessment Rubric Guide
   ═════════════════════════════════════════════════════════════════ */
const RUBRIC_GROUPS = [
    {
        icon: VerifiedUserIcon, color: GREEN,
        title: 'Child Online Safety Readiness',
        desc: 'Preparedness to act safely, responsibly, and effectively online.',
        bands: [
            {name: 'High',     color: READINESS_COLORS.High,     meaning: 'Strong, consistent readiness to participate online safely, responsibly, and effectively.'},
            {name: 'Moderate', color: READINESS_COLORS.Moderate, meaning: 'Generally adequate readiness, with some areas for reinforcement.'},
            {name: 'Basic',    color: READINESS_COLORS.Basic,    meaning: 'Partial or inconsistent readiness; targeted support may be needed.'},
            {name: 'Low',      color: READINESS_COLORS.Low,      meaning: 'Limited readiness or risky gaps requiring priority attention.'},
        ],
    },
    {
        icon: ReportProblemIcon, color: ORANGE,
        title: 'Child Cyber-Risk Experiences *',
        desc: 'Reported exposure to, involvement in, or impact from cyber risks.',
        bands: [
            {name: 'No or Minimal Risk', color: RISK_COLORS[0], meaning: 'No reported risk, or the reported risk level is minimal.'},
            {name: 'Low Risk',           color: RISK_COLORS[1], meaning: 'Some exposure or incident reported, but limited in frequency or severity.'},
            {name: 'Moderate Risk',      color: RISK_COLORS[2], meaning: 'Repeated, elevated, or concerning risk pattern reported.'},
            {name: 'High Risk',          color: RISK_COLORS[3], meaning: 'Frequent, serious, direct, coercive, harmful, or highly disruptive risk reported.'},
        ],
    },
    {
        icon: PeopleIcon, color: BLUE,
        title: 'Context & Support',
        desc: 'Access, guidance, school education, home support, and help-seeking conditions.',
        bands: [
            {name: 'Strong Enabling Context',   color: CONTEXT_COLORS[0], meaning: 'Strong access, support, guidance, or protective conditions.'},
            {name: 'Adequate Enabling Context', color: CONTEXT_COLORS[1], meaning: 'Generally positive conditions, with some minor limitations.'},
            {name: 'Limited Enabling Context',  color: CONTEXT_COLORS[2], meaning: 'Partial, inconsistent, or uncertain support conditions.'},
            {name: 'Low Enabling Context',      color: CONTEXT_COLORS[3], meaning: 'Weak, insufficient, or absent support conditions.'},
        ],
    },
    {
        icon: StorageIcon, color: '#4B5563',
        title: 'Participation and Data Confidence',
        desc: 'Where the assessment was deployed, how fresh the data are, and how far the results can be trusted.',
        bands: [
            {name: 'Active',    color: '#1B6B55', meaning: 'Deployment is active and operational; results can be used.'},
            {name: 'Growth',    color: '#4A9278', meaning: 'Deployment is expanding; results are usable but still building coverage.'},
            {name: 'Low cov',   color: '#7B9040', meaning: 'Coverage is below threshold; interpret comparisons with caution.'},
            {name: 'High risk', color: ORANGE,    meaning: 'High-risk pattern detected; prioritize support or safeguarding action.'},
            {name: 'Data weak', color: '#9CA3AF', meaning: 'Data are too limited, stale, or uneven to support confident decisions.'},
        ],
    },
];

const RubricCard = ({group}) => {
    const Icon = group.icon;
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
        }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1}}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', backgroundColor: `${group.color}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Icon style={{color: group.color, fontSize: 22}}/>
                </Box>
                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{group.title}</Typography>
            </Box>
            <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6, mb: 2}}>{group.desc}</Typography>

            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                {group.bands.map((b) => (
                    <Box key={b.name}>
                        <Box sx={{display: 'inline-flex', mb: 0.6}}>
                            <Chip text={b.name} color={b.color}/>
                        </Box>
                        <Typography sx={{fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5}}>{b.meaning}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export const RubricGuideSection = () => (
    <Box>
        <SectionHeader
            title="COS Children Assessment Rubric Guide"
            subtitle="What is measured, and how should it be interpreted?"
        />
        <Grid container spacing={3} alignItems="stretch">
            {RUBRIC_GROUPS.map((g) => (
                <Grid item xs={12} sm={6} md={3} key={g.title}>
                    <RubricCard group={g}/>
                </Grid>
            ))}
        </Grid>
        <NoteBanner
            label="Decision guidance:"
            text={[
                'Readiness scores describe student preparedness.',
                'Risk scores describe reported risk experience.',
                'Context scores describe conditions around the child, not the child’s competency.',
                'Deployment & data quality show how widely the assessment was deployed and how confidently the results can be used.',
            ]}
        />
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Slide 5 — How COS Children Assessment Scores Are Structured
   ═════════════════════════════════════════════════════════════════ */
const SCORE_STEPS = [
    {num: '1', title: 'Questions',        body: 'What students answer in the survey.'},
    {num: '2', title: 'Item Scores',      body: 'Related answers are summarized into diagnostic topics.'},
    {num: '3', title: 'Area Scores',      body: 'Items are grouped into broader readiness, risk, and context areas.'},
    {num: '4', title: 'Composite Scores', body: 'Results show strengths, risks, and action priorities.'},
];
const SCORE_STATS = [
    {value: '179', label: 'Questions', body: 'Survey questions covering readiness, risks, and environment.'},
    {value: '37',  label: 'Items',     body: 'Diagnostic topics that combine related responses.'},
    {value: '12',  label: 'Areas',     body: 'Broader groups used to summarize child online safety readiness, risks, and context and supports.'},
];

export const ScoreStructureSection = () => (
    <Box>
        <SectionHeader
            title="How COS Children Assessment Scores Are Structured"
            subtitle="COS Children Assessment scores are built step by step. Survey answers are summarized into item, area, and composite scores to help you identify strengths, risks, and action priorities."
        />

        <Grid container spacing={2.5} alignItems="stretch">
            {SCORE_STEPS.map((s, i) => (
                <Grid item xs={12} sm={6} md={3} key={s.num}>
                    <Box sx={{
                        position: 'relative', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                    }}>
                        <Box sx={{
                            width: 34, height: 34, borderRadius: '50%', backgroundColor: GREEN,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                        }}>
                            <Typography sx={{fontSize: '16px', fontWeight: 800, color: '#fff'}}>{s.num}</Typography>
                        </Box>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>{s.title}</Typography>
                        <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{s.body}</Typography>
                        {i < SCORE_STEPS.length - 1 && (
                            <Typography sx={{
                                display: {xs: 'none', md: 'block'}, position: 'absolute', right: -14, top: '50%',
                                transform: 'translateY(-50%)', color: '#CBD5E1', fontSize: '22px', fontWeight: 800, zIndex: 1,
                            }}>
                                ›
                            </Typography>
                        )}
                    </Box>
                </Grid>
            ))}
        </Grid>

        <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mt: 4, mb: 2}}>
            Structures Used in the COS Children Assessment
        </Typography>
        <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} sm={4}>
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                }}>
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 1}}>
                        <Typography sx={{fontSize: '34px', fontWeight: 800, color: TIER_COLOR.Exceptional, lineHeight: 1}}>
                            179
                        </Typography>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>Questions</Typography>
                    </Box>
                    <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>Survey questions covering readiness, risks, and environment.</Typography>
                </Box>
            </Grid>
            {/* Items before Areas, as the Common Section deck (p.8) orders them. */}
            <Grid item xs={12} sm={4}>
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                }}>
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 1}}>
                        <Typography sx={{fontSize: '34px', fontWeight: 800, color: TIER_COLOR.Exceptional, lineHeight: 1}}>
                            37
                        </Typography>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>Items</Typography>
                    </Box>
                    <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>Diagnostic topics that combine related responses.</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                }}>
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 1}}>
                        <Typography sx={{fontSize: '34px', fontWeight: 800, color: TIER_COLOR.Exceptional, lineHeight: 1}}>
                            12
                        </Typography>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>Areas</Typography>
                    </Box>
                    <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>Broader groups used to summarize child online safety readiness, risks, and context and supports.</Typography>
                </Box>
            </Grid>
        </Grid>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Slide 7 — How COS Readiness Program Scores Are Structured (P1/LC)
   ═════════════════════════════════════════════════════════════════ */
const READINESS_SCORE_TYPES = [
    {
        title: 'COS Baseline Score',
        body: 'Defines initial COS readiness level and identifies priority areas for improvement.',
        accent: BLUE,
    },
    {
        title: 'Learning Impact Signal',
        body: 'Measures learning program impact using pre- and post-assessment evidence to show students’ progress in targeted COS areas.',
        accent: READINESS_BAND_COLORS.Moderate,
    },
    {
        title: 'COS Reassessment Score',
        body: 'Confirms how much COS readiness has improved since the baseline assessment.',
        accent: GREEN,
    },
];

export const ReadinessScoreStructureSection = () => (
    <Box>
        <SectionHeader
            title="How Child Online Safety (COS) Readiness Solution Scores Are Structured"
        />
        <Grid container spacing={2.5} alignItems="stretch">
            {READINESS_SCORE_TYPES.map((s) => (
                <Grid item xs={12} md={4} key={s.title}>
                    <Box sx={{
                        display: 'flex', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
                        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        overflow: 'hidden', height: '100%',
                    }}>
                        <Box sx={{width: '5px', flexShrink: 0, backgroundColor: s.accent}}/>
                        <Box sx={{p: 2.5, flex: 1}}>
                            <Typography sx={{
                                fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.6px',
                                textTransform: 'uppercase', mb: 1.5,
                            }}>
                                {s.title}
                            </Typography>
                            <Box sx={{
                                backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '8px', p: 2,
                            }}>
                                <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6, textAlign: 'center'}}>
                                    {s.body}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Box>
);

const parseLeftLineHtml = (html) => {
    if (!html) return null;
    const titleMatch = html.match(/<h1>(.*?)<\/h1>/i);
    const title = titleMatch ? titleMatch[1] : '';
    const items = [];
    const liMatches = html.matchAll(/<li>([\s\S]*?)<\/li>/gi);
    for (const match of liMatches) {
        items.push(match[1]);
    }
    return { title, items };
};

/* ══════════════════════════════════════════════════════════════════
   Slide 6 — What This Report Tells You
   ═════════════════════════════════════════════════════════════════ */
const REPORT_CARDS = [
    {
        barColor: '#1B6B55', label: 'This report helps you understand',
        items: [
            'Which countries and schools are active, effective or at risk.',
            'Where deployment is dormant or under-developed.',
            'Where the partner is producing measurable improvement.',
            'Where data is too thin to report or compare.',
        ],
    },
    {
        barColor: '#3B6FA0', label: 'Use it to decide',
        items: [
            'Where to deploy, activate, support or expand.',
            'Which schools need implementation or safeguarding support.',
            'Which deployments need a re-assessment before reporting.',
            'Where the renewal or expansion opportunity sits.',
        ],
    },
    {
        barColor: '#DC2626', label: 'Do not use it to',
        items: [
            'Rank schools as a public league table.',
            'Treat program participants as a representative population.',
            'Compare deployments that have insufficient data.',
            'Read cyber-risk prevalence as a competency score.',
        ],
    },
];

const DEFAULT_COLORS = ['#1B6B55', '#3B6FA0', '#DC2626'];

const CARD_SECTION_USING_REPORT = 'S_3';

export const UsingReportSection = () => {
    const { dashboardData } = useDashboard({ card_sections: CARD_SECTION_USING_REPORT });
    const sectionData = dashboardData?.cards?.sections?.find(s => s.section_code === CARD_SECTION_USING_REPORT);
    
    const titleCard = sectionData?.cards?.find(c => c.card_type === 'TitleCard');
    const leftLineCards = sectionData?.cards?.filter(c => c.card_type === 'LeftLineCard') || [];
    
    const displayTitle = titleCard?.title || "What This Report Tells You";
    const displaySubtitle = titleCard?.footer ? (
        <>
            {/*<strong style={{ fontStyle: 'normal', color: NAVY, fontWeight: 800 }}>Decision question: </strong>*/}
            {titleCard.footer}
        </>
    ) : "How should you use this report?";
    const hasDynamicCards = leftLineCards.length > 0;

    return (
        <Box>
            <SectionHeader
                title={displayTitle}
                subtitle={displaySubtitle}
            />
            <Grid container spacing={3} alignItems="stretch" sx={{mt: 2}}>
                {hasDynamicCards ? (
                    leftLineCards.map((card, index) => {
                        const parsed = parseLeftLineHtml(card.leftline_html);
                        const cardTitle = parsed ? parsed.title : '';
                        const cardItems = parsed ? parsed.items : [];
                        const barColor = (card.accent_color && card.accent_color !== '#000000') 
                            ? card.accent_color 
                            : DEFAULT_COLORS[index % DEFAULT_COLORS.length];

                        return (
                            <Grid item xs={12} md={4} key={card.card_code || index}>
                                <Box sx={{
                                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
                                    minHeight: {md: '520px'}, display: 'flex', flexDirection: 'column',
                                }}>
                                    <Box sx={{backgroundColor: barColor, height: '6px', flexShrink: 0}}/>
                                    <Box sx={{p: {xs: 3, md: 4}, display: 'flex', flexDirection: 'column', flex: 1}}>
                                        <Typography sx={{
                                            fontSize: '17px', fontWeight: 700, color: barColor, mb: 3, lineHeight: 1.3,
                                        }}>
                                            {cardTitle}
                                        </Typography>
                                        <Box component="ul" sx={{m: 0, pl: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5}}>
                                            {cardItems.map((item, i) => (
                                                <Box component="li" key={i}>
                                                    <Typography sx={{fontSize: '14.5px', color: '#374151', lineHeight: 1.65}}>{item}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })
                ) : (
                    REPORT_CARDS.map((card) => (
                        <Grid item xs={12} md={4} key={card.label}>
                            <Box sx={{
                                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
                                minHeight: {md: '520px'}, display: 'flex', flexDirection: 'column',
                            }}>
                                <Box sx={{backgroundColor: card.barColor, height: '6px', flexShrink: 0}}/>
                                <Box sx={{p: {xs: 3, md: 4}, display: 'flex', flexDirection: 'column', flex: 1}}>
                                    <Typography sx={{
                                        fontSize: '17px', fontWeight: 700, color: card.barColor, mb: 3, lineHeight: 1.3,
                                    }}>
                                        {card.label}
                                    </Typography>
                                    <Box component="ul" sx={{m: 0, pl: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5}}>
                                        {card.items.map((item) => (
                                            <Box component="li" key={item}>
                                                <Typography sx={{fontSize: '14.5px', color: '#374151', lineHeight: 1.65}}>{item}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    ))
                )}
            </Grid>
        </Box>
    );
};
